import React from 'react';
import GradeDistributionChart from './charts/GradeDistributionChart.js';
import FlashProbabilityChart from './charts/FlashProbabilityChart.js';
import GradeProgressionChart from './charts/GradeProgressionChart.js';
import RoutesPerYearChart from './charts/RoutesPerYearChart.js';
import ProjectTracker from './charts/ProjectTracker.js';
import CragPerformance from './charts/CragPerformance.js';
import CragStatsChart  from './charts/CragStatsChart.js';
import AscentTypesChart from './charts/AscentTypesChart.js';
import AscentProgressionChart from './charts/AscentProgressionChart.js';
import BestClimbsChart from './charts/BestClimbsChart.js';
import ClimbingPartnersChart from './charts/ClimbingPartnersChart.js';
import HeatmapChart from './charts/HeatmapChart.js';
import CGPCGEChart from './charts/CGPCGEChart.js';
import CGPPerGradeChart from './charts/CGPPerGradeChart.js';
import { formatGrade } from '@utils/gradeUtils.js';

export const ChartRenderer = ({
  selectedMetric,
  chartData,
  filteredData,
  climbingStyle,
  gradeSystem,
  onGradeClick,
  isHorizontal,
  onLayoutChange,
  showDistinctRoutes,  
  selectedStat,
  numCrags 
}) => {
  if (!filteredData) {
    return <div className="text-center py-8">No data available</div>;
  }

  // Create grade formatting functions that will be used by multiple charts
  const formatGradeWithStyle = (grade) => formatGrade(grade, gradeSystem, climbingStyle);
  const getGradeLabel = formatGradeWithStyle;

  const charts = {
    grades: () => (
      <GradeDistributionChart 
        data={filteredData}
        climbingStyle={climbingStyle}
        gradeSystem={gradeSystem}
        formatGradeWithStyle={formatGradeWithStyle}
        onGradeClick={onGradeClick}
        isHorizontal={isHorizontal}
        onLayoutChange={onLayoutChange}
        showDistinctRoutes={showDistinctRoutes}  // Pass it through
      />
    ),
    flash: () => (
      <FlashProbabilityChart 
        data={filteredData}
        climbingStyle={climbingStyle}
        gradeSystem={gradeSystem}
        formatGradeWithStyle={formatGradeWithStyle}
      />
    ),
    progression: () => (
      <GradeProgressionChart 
        data={chartData}
        getGradeLabel={getGradeLabel}
        climbingStyle={climbingStyle}
        gradeSystem={gradeSystem}
      />
    ),
    ascents: () => (
      <AscentProgressionChart 
        data={filteredData}
        getGradeLabel={getGradeLabel}
        climbingStyle={climbingStyle}
        gradeSystem={gradeSystem}
      />
    ),
    routesPerYear: () => (
      <RoutesPerYearChart 
        data={filteredData}
        climbingStyle={climbingStyle}
        selectedStat={selectedStat} 
      />
    ),
    projects: () => (
      <ProjectTracker 
        data={filteredData}
        climbingStyle={climbingStyle}
        gradeSystem={gradeSystem}
      />
    ),
    cragPerformance: () => (
      <CragPerformance 
        data={filteredData}
        climbingStyle={climbingStyle}
      />
    ),
    crags: () => (
      <CragStatsChart
        data={filteredData}
        climbingStyle={climbingStyle}
        selectedStat={selectedStat}
        numCrags={numCrags}
      />
    ),
    types: () => (
      <AscentTypesChart 
        data={chartData}
        climbingStyle={climbingStyle}
      />
    ),
    bestClimbs: () => (
      <BestClimbsChart
        data={filteredData}
        climbingStyle={climbingStyle}
        getGradeLabel={getGradeLabel}
        gradeSystem={gradeSystem}
      />
    ),
    heatmap: () => (
      <HeatmapChart
        data={filteredData}
        climbingStyle={climbingStyle}
        gradeSystem={gradeSystem}
      />
    ),
    partners: () => (
      <ClimbingPartnersChart
        data={filteredData}
        climbingStyle={climbingStyle}
        selectedStat={selectedStat}
        numPartners={numCrags}
      />
    ),
    cgpCge: () => (
      <CGPCGEChart
        data={filteredData}
        climbingStyle={climbingStyle}
      />
    ),
    cgpPerGrade: () => (
      <CGPPerGradeChart
        data={filteredData}
        climbingStyle={climbingStyle}
        gradeSystem={gradeSystem}
        formatGradeWithStyle={formatGradeWithStyle}
      />
    )
  };

  const ChartComponent = charts[selectedMetric];
  return ChartComponent ? <ChartComponent /> : null;
};